<script setup lang="ts">
interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface LocationResult {
  placeId: string;
  formattedAddress: string;
  addressComponents: any[];
  lat: number;
  lng: number;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Search for a location...",
  disabled: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "location-selected": [location: LocationResult];
  "location-cleared": [];
}>();

const config = useRuntimeConfig();
const autocompleteRef = ref<HTMLElement>();
const placeAutocomplete = ref<any>();
const hasValue = ref(false);

const searchQuery = computed({
  get: () => props.modelValue || "",
  set: (value: string) => emit("update:modelValue", value),
});

// Clear search function
const clearSearch = () => {
  if (placeAutocomplete.value) {
    // Try multiple approaches to clear the Google Places input
    try {
      // Method 1: Clear the component value
      placeAutocomplete.value.value = "";

      // Method 2: Access through shadow DOM
      const shadowRoot = placeAutocomplete.value.shadowRoot;
      if (shadowRoot) {
        const inputElement = shadowRoot.querySelector("input");
        if (inputElement) {
          inputElement.value = "";
          inputElement.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }

      // Method 3: Try direct querySelector as fallback
      const inputElement = placeAutocomplete.value.querySelector("input");
      if (inputElement) {
        inputElement.value = "";
        inputElement.dispatchEvent(new Event("input", { bubbles: true }));
      }
    } catch (error) {
      console.error("Error clearing Google input:", error);
    }
  }

  // Always clear our reactive state
  searchQuery.value = "";
  hasValue.value = false;
  emit("location-cleared");
};

// Initialize Google Places API with new PlaceAutocompleteElement
onMounted(async () => {
  if (!config.public.googleMapsApiKey) {
    console.warn("Google Maps API key not configured");
    return;
  }

  try {
    // Use the new Places API
    const { $gMapLoader } = useNuxtApp();
    await $gMapLoader().importLibrary("places");

    // Create the new PlaceAutocompleteElement with options
    placeAutocomplete.value = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ["za"], // Restrict to South Africa
      // includedPrimaryTypes: ["(regions)"], // Focus on regions/cities
    });

    // Configure the element
    placeAutocomplete.value.placeholder = props.placeholder;

    // Add event listener for place selection
    placeAutocomplete.value.addEventListener("gmp-select", async ({ placePrediction }: any) => {
      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ["id", "formattedAddress", "addressComponents", "location"],
        });

        const location: LocationResult = {
          placeId: place.id!,
          formattedAddress: place.formattedAddress!,
          addressComponents: place.addressComponents || [],
          lat: place.location!.lat(),
          lng: place.location!.lng(),
        };

        searchQuery.value = place.formattedAddress!;
        hasValue.value = true;
        emit("location-selected", location);
      } catch (error) {
        console.error("Error getting place details:", error);
      }
    });

    // Listen for input changes to detect when cleared
    placeAutocomplete.value.addEventListener("input", (event: any) => {
      const inputValue = event.target.value;
      hasValue.value = !!inputValue;

      if (!inputValue) {
        // Input was cleared, reset search query and emit cleared event
        searchQuery.value = "";
        emit("location-cleared");
      }
    });

    placeAutocomplete.value.addEventListener("clear", function () {
      const place = placeAutocomplete.value.getPlace();
      console.log("Selected place:", place);
    });

    // Append to our container
    if (autocompleteRef.value) {
      autocompleteRef.value.appendChild(placeAutocomplete.value);
    }
  } catch (error) {
    console.error("Failed to initialize Google Places:", error);
  }
});

// Helper function to set the autocomplete value
const setAutocompleteValue = (value: string) => {
  if (!placeAutocomplete.value) return false;

  try {
    const shadowRoot = placeAutocomplete.value.shadowRoot;
    if (shadowRoot) {
      const inputElement = shadowRoot.querySelector("input");
      if (inputElement) {
        inputElement.value = value;
        hasValue.value = true;
        return true;
      }
    }
  } catch (error) {
    // Silent failure - autocomplete will still work
  }
  return false;
};

// Watch for modelValue changes and update the autocomplete input
watch(
  () => props.modelValue,
  async (newValue) => {
    if (!newValue) return;

    // If autocomplete isn't ready yet, wait for it
    if (!placeAutocomplete.value) {
      // Wait up to 2 seconds for autocomplete to initialize
      for (let i = 0; i < 20; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (placeAutocomplete.value) {
          break;
        }
      }
    }

    if (placeAutocomplete.value) {
      setAutocompleteValue(newValue);
    }
  }
);
</script>

<template>
  <div class="relative">
    <!-- Google Places Autocomplete Element Container -->
    <div ref="autocompleteRef" class="w-full" :class="{ 'opacity-50 pointer-events-none': disabled }">
      <!-- The PlaceAutocompleteElement will be appended here -->
    </div>

    <!-- Custom clear button -->
    <button
      v-if="hasValue && !disabled"
      @click="clearSearch"
      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
      type="button"
      tabindex="-1"
    >
      <Icon name="material-symbols:close" class="w-4 h-4" />
    </button>
  </div>
</template>
